import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThemeContext } from 'styled-components'
import { Edit2 } from 'react-feather'
import { ethers } from 'ethers'
import { Text } from 'rebass'
import { ButtonSecondary } from '../../components/Button'
import { AutoColumn } from '../../components/Column'
import { RowBetween } from '../../components/Row'
import { TYPE } from '../../theme'
import { Input as NumericalInput } from '../../components/NumericalInput'
import { useActiveWeb3React } from '../../hooks'
import { useWalletModalToggle } from '../../state/application/hooks'
import {
  useCurrentPrice,
  useCreateBuyOrder,
  useCreateSellOrder,
  useApproveUSDT,
  useUsdtAllowance,
  useUsdtBalance,
  useOrderBookConfig,
  useWhitelistStatus,
  formatPrice
} from '../../hooks/useOrderBook'
import { OrderType, USDT_DECIMALS, FEE_RATE_BASE, MAX_PRICE_MULTIPLIER } from '../../constants/orderbook'
import {
  OrderWrapper,
  TabRowPill,
  TabButtonPill,
  InputPanel,
  Container,
  LabelRow,
  InputRow,
  InfoRow,
  UnitLabel,
  InfoLabel,
  InfoValue,
  PriceHighlight,
  FeeRateRow,
  FeeRateRight,
  FeeRateChangeBtn
} from './styleds'
import FeeRateModal from './FeeRateModal'
import TransactionConfirmationModal from '../../components/TransactionConfirmationModal'

interface OrderFormProps {
  onOrderCreated: () => void
  /** 变化时触发刷新 USDT / MUD 余额（如取消订单后、点击列表刷新后） */
  balanceRefreshTrigger?: number
}

/** 去掉末尾多余的 0，如 6818.0000 → 6818，5204.18288680 → 5204.1828868 */
function trimTrailingZeros(s: string): string {
  if (s === '' || s == null) return s
  const t = s.replace(/\.?0+$/, '')
  return t === '' ? '0' : t
}

/** 依赖侧展示用：先四舍五入再格式化，避免浮点误差出现 675.9999 */
function formatDependentUsdt(num: number): string {
  if (num <= 0 || !Number.isFinite(num)) return ''
  const rounded = Math.round(num * 10000) / 10000
  return trimTrailingZeros(rounded.toFixed(4))
}

function formatDependentMud(num: number): string {
  if (num <= 0 || !Number.isFinite(num)) return ''
  const rounded = Math.round(num * 1e8) / 1e8
  return trimTrailingZeros(rounded.toFixed(8))
}

type AmountField = 'amount' | 'usdtAmount'

export default function OrderForm({ onOrderCreated, balanceRefreshTrigger }: OrderFormProps) {
  const { t } = useTranslation()
  const { account, library } = useActiveWeb3React()
  const theme = useContext(ThemeContext)
  const toggleWalletModal = useWalletModalToggle()

  // State（参考兑换：独立字段 + 用户输入字符串，避免交易额删除键被计算值覆盖成 675.9999）
  const [orderType, setOrderType] = useState<OrderType>(OrderType.BUY)
  const [priceInput, setPriceInput] = useState('')
  const [independentField, setIndependentField] = useState<AmountField>('amount')
  const [typedValue, setTypedValue] = useState('')
  const [feeRate, setFeeRate] = useState('1')
  const [feeRateModalOpen, setFeeRateModalOpen] = useState(false)
  const [txConfirmOpen, setTxConfirmOpen] = useState(false)
  const [txConfirmPending, setTxConfirmPending] = useState(false)
  const [txConfirmHash, setTxConfirmHash] = useState<string | undefined>(undefined)

  // Hooks
  const { price: currentPrice, loading: priceLoading, lastUpdated: priceLastUpdated } = useCurrentPrice()
  const [priceUpdatedAgo, setPriceUpdatedAgo] = useState('')

  useEffect(() => {
    const tick = () => {
      if (!priceLastUpdated) {
        setPriceUpdatedAgo(t('orderPriceUpdatedSeconds', { count: '0.5' }))
        return
      }
      const sec = (Date.now() - priceLastUpdated) / 1000
      if (sec < 60) {
        const val = Math.max(0.5, Math.floor(sec / 0.5) * 0.5).toFixed(1)
        setPriceUpdatedAgo(t('orderPriceUpdatedSeconds', { count: val }))
      } else {
        setPriceUpdatedAgo(t('orderPriceUpdatedMinutes', { count: Math.floor(sec / 60) }))
      }
    }
    tick()
    const id = setInterval(tick, 500)
    return () => clearInterval(id)
  }, [t, priceLastUpdated])
  const { createBuyOrder, pending: buyPending } = useCreateBuyOrder()
  const { createSellOrder, pending: sellPending } = useCreateSellOrder()
  const { approve: approveUSDT, pending: approvePending } = useApproveUSDT()
  const { allowance, refresh: refreshAllowance } = useUsdtAllowance()
  const { balance: usdtBalance, formatted: usdtFormatted, refresh: refreshUsdtBalance } = useUsdtBalance()
  const config = useOrderBookConfig()
  const isWhitelisted = useWhitelistStatus()

  // MUD balance
  const [mudBalance, setMudBalance] = React.useState('0')
  const fetchMudBalance = React.useCallback(() => {
    if (!account || !library) return
    library
      .getBalance(account)
      .then((bal: ethers.BigNumber) => {
        setMudBalance(ethers.utils.formatEther(bal))
      })
      .catch(() => {})
  }, [account, library])
  React.useEffect(() => {
    fetchMudBalance()
  }, [fetchMudBalance])

  // 父组件触发余额刷新（取消订单、点击列表刷新等）
  React.useEffect(() => {
    if (balanceRefreshTrigger == null || balanceRefreshTrigger <= 0) return
    refreshUsdtBalance()
    fetchMudBalance()
  }, [balanceRefreshTrigger, refreshUsdtBalance, fetchMudBalance])

  const isBuy = orderType === OrderType.BUY
  const isPending = buyPending || sellPending

  // 买入/卖出切换时清空数量与交易额
  React.useEffect(() => {
    setTypedValue('')
  }, [orderType])

  // Set current price as default
  React.useEffect(() => {
    if (currentPrice && currentPrice !== '0' && !priceInput) {
      setPriceInput(formatPrice(currentPrice))
    }
  }, [currentPrice]) // eslint-disable-line react-hooks/exhaustive-deps

  const feeRateNum = parseInt(feeRate) || 0
  const priceNum = parseFloat(priceInput) || 0

  // 与兑换一致：当前编辑的框为独立字段，另一侧由计算得出
  const effectiveAmount =
    independentField === 'amount'
      ? parseFloat(typedValue) || 0
      : priceNum > 0
      ? (parseFloat(typedValue) || 0) / priceNum
      : 0
  const effectiveUsdt = independentField === 'usdtAmount' ? parseFloat(typedValue) || 0 : effectiveAmount * priceNum
  // 向下取整到 6 位小数，避免 toFixed 四舍五入后超过可用余额
  const usdtAmountTruncated = Math.floor(effectiveUsdt * Math.pow(10, USDT_DECIMALS)) / Math.pow(10, USDT_DECIMALS)

  const maxMud = Math.max(0, parseFloat(mudBalance) - 0.01)
  // 买入时用链上精确 USDT 余额，避免进度条 100% 时因精度/四舍五入导致“余额不足”
  const usdtBalanceExact = parseFloat(ethers.utils.formatUnits(usdtBalance.toString(), USDT_DECIMALS)) || 0

  const needsApproval = useMemo(() => {
    if (!isBuy || !typedValue || effectiveAmount <= 0 || priceNum <= 0) return false
    try {
      const usdtBN = ethers.utils.parseUnits(usdtAmountTruncated.toFixed(USDT_DECIMALS), USDT_DECIMALS)
      return allowance.lt(usdtBN)
    } catch {
      return false
    }
  }, [isBuy, typedValue, effectiveAmount, allowance, usdtAmountTruncated, priceNum])

  const estimatedReceive = useMemo(() => {
    if (!priceInput || !typedValue || priceNum <= 0 || effectiveAmount <= 0) return ''
    try {
      const feeFactor = 1 - feeRateNum / FEE_RATE_BASE
      if (isBuy) {
        return (effectiveAmount * feeFactor).toFixed(4) + ' MUD'
      } else {
        return (effectiveUsdt * feeFactor).toFixed(2) + ' USDT'
      }
    } catch {
      return ''
    }
  }, [priceInput, typedValue, isBuy, feeRateNum, effectiveAmount, effectiveUsdt, priceNum])

  const validationError = useMemo(() => {
    if (!priceInput || !typedValue) return null
    if (priceNum <= 0) return t('orderPriceTooLow')
    if (effectiveAmount <= 0) return t('orderAmountTooLow')
    const currentPriceNum =
      currentPrice && currentPrice !== '0' ? parseFloat(currentPrice) / Math.pow(10, USDT_DECIMALS) : 0
    if (currentPriceNum > 0) {
      if (isBuy) {
        const minBuyPrice = currentPriceNum / MAX_PRICE_MULTIPLIER
        if (priceNum < minBuyPrice) return t('orderPriceBelowMinBuy', { min: minBuyPrice.toFixed(4) })
      } else {
        const maxSellPrice = currentPriceNum * MAX_PRICE_MULTIPLIER
        if (priceNum > maxSellPrice) return t('orderPriceAboveMaxSell', { max: maxSellPrice.toFixed(4) })
      }
    }
    if (isBuy) {
      try {
        const usdtBN = ethers.utils.parseUnits(usdtAmountTruncated.toFixed(USDT_DECIMALS), USDT_DECIMALS)
        if (usdtBN.gt(usdtBalance)) return t('orderInsufficientBalance')
      } catch {
        return t('inputNotValid')
      }
      if (effectiveUsdt < config.minOrderValueUSDT) return t('orderAmountBelowMin', { value: config.minOrderValueUSDT })
    } else {
      if (effectiveAmount > maxMud) return t('orderInsufficientBalance')
      if (effectiveUsdt < config.minOrderValueUSDT) return t('orderAmountBelowMin', { value: config.minOrderValueUSDT })
    }
    return null
  }, [
    priceInput,
    typedValue,
    isBuy,
    usdtBalance,
    maxMud,
    config,
    effectiveUsdt,
    usdtAmountTruncated,
    effectiveAmount,
    priceNum,
    currentPrice,
    t
  ])

  // 挂单价格最多 4 位小数
  const setPriceInputWithMaxDecimals = useCallback((value: string) => {
    if (value === '') {
      setPriceInput('')
      return
    }
    const parts = value.split('.')
    if (parts.length === 2 && parts[1].length > 4) {
      setPriceInput(parts[0] + '.' + parts[1].slice(0, 4))
      return
    }
    setPriceInput(value)
  }, [])

  const handleAmountInput = useCallback((value: string) => {
    setIndependentField('amount')
    setTypedValue(value)
  }, [])

  const handleUsdtAmountInput = useCallback((value: string) => {
    if (value !== '') {
      const parts = value.split('.')
      if (parts.length === 2 && parts[1].length > USDT_DECIMALS) {
        value = parts[0] + '.' + parts[1].slice(0, USDT_DECIMALS)
      }
    }
    setIndependentField('usdtAmount')
    setTypedValue(value)
  }, [])

  const handleApprove = useCallback(async () => {
    try {
      const tx = await approveUSDT()
      if (tx) {
        await tx.wait()
        refreshAllowance()
      }
    } catch (e) {
      console.error('Approve failed:', e)
    }
  }, [approveUSDT, refreshAllowance])

  const handleCreateOrder = useCallback(async () => {
    if (!priceInput || !typedValue || validationError) return
    setTxConfirmOpen(true)
    setTxConfirmPending(true)
    setTxConfirmHash(undefined)
    try {
      let tx
      if (isBuy) {
        const usdtAmountStr = usdtAmountTruncated.toFixed(USDT_DECIMALS)
        tx = await createBuyOrder(priceInput, usdtAmountStr, feeRateNum, true)
      } else {
        const mudAmountStr = effectiveAmount.toFixed(8)
        tx = await createSellOrder(priceInput, mudAmountStr, feeRateNum, true)
      }
      if (tx) {
        setTxConfirmHash(tx.hash)
        setTxConfirmPending(false)
        tx.wait()
          .then(() => {
            setTypedValue('')
            refreshAllowance()
            onOrderCreated()
          })
          .catch(() => {})
      } else {
        setTxConfirmOpen(false)
      }
    } catch (e) {
      console.error('Create order failed:', e)
      setTxConfirmOpen(false)
    }
  }, [
    priceInput,
    typedValue,
    validationError,
    isBuy,
    feeRateNum,
    usdtAmountTruncated,
    effectiveAmount,
    createBuyOrder,
    createSellOrder,
    refreshAllowance,
    onOrderCreated
  ])

  // Fee rate options
  const minFee = isWhitelisted ? 0 : config.minFeeRate

  // 费率：白名单用户可选 0.0%；否则 0.1% 起按 0.1% 递增到 0.5%，然后到 1%（最大 1%）；1% 选项右侧说明「冰山单不对外公开」
  const feeOptions = useMemo(() => {
    const options: { value: string; label: string; suffix?: string }[] = []
    const rates = minFee === 0 ? [0, 1, 2, 3, 4, 5, 10] : [1, 2, 3, 4, 5, 10] // 白名单可选 0.0%
    for (const r of rates) {
      if (r >= minFee) {
        const label = `${(r / 10).toFixed(1)}%`
        const suffix = r === 10 ? t('orderFeeRateIcebergSuffix') : undefined
        options.push({ value: r.toString(), label, ...(suffix ? { suffix } : {}) })
      }
    }
    if (options.length === 0) {
      options.push({ value: minFee.toString(), label: `${(minFee / 10).toFixed(1)}%` })
    }
    return options
  }, [minFee, t])

  // 公开/私有切换时，手续费率切到该模式下的最低档：公开 0.1%，私有 1%
  React.useEffect(() => {
    setFeeRate(minFee.toString())
  }, [minFee])

  return (
    <>
      <OrderWrapper>
        {/* Buy/Sell Toggle */}
        <TabRowPill>
          <TabButtonPill active={isBuy} onClick={() => setOrderType(OrderType.BUY)}>
            {t('orderBuyMUD')}
          </TabButtonPill>
          <TabButtonPill active={!isBuy} onClick={() => setOrderType(OrderType.SELL)}>
            {t('orderSellMUD')}
          </TabButtonPill>
        </TabRowPill>

        {/* Current Price：上减 5px，下多 4px；括号内为数据时间 */}
        <InfoRow style={{ marginTop: -6, marginBottom: 5 }}>
          <InfoLabel>
            {t('orderCurrentPrice')}({priceUpdatedAgo})
          </InfoLabel>
          <PriceHighlight>
            {priceLoading ? '...' : currentPrice !== '0' ? formatPrice(currentPrice) : '--'} USDT/MUD
          </PriceHighlight>
        </InfoRow>

        {/* 价格(USDT) */}
        <InputPanel>
          <Container>
            <LabelRow>
              <RowBetween>
                <TYPE.body color={theme.text2} fontWeight={500} fontSize={14}>
                  {t('orderPrice')}(USDT)
                </TYPE.body>
              </RowBetween>
            </LabelRow>
            <InputRow>
              <NumericalInput
                className="order-price-input"
                value={priceInput}
                onUserInput={setPriceInputWithMaxDecimals}
                placeholder="0.0"
              />
              <UnitLabel>USDT</UnitLabel>
            </InputRow>
          </Container>
        </InputPanel>

        {/* 数量(MUD)，余额显示在输入框所在行；卖出时点击余额填入全部 MUD */}
        <InputPanel>
          <Container>
            <LabelRow>
              <RowBetween>
                <TYPE.body color={theme.text2} fontWeight={500} fontSize={14}>
                  {t('orderAmount')}(MUD)
                </TYPE.body>
                <TYPE.body
                  color={theme.text2}
                  fontSize={16}
                  fontWeight={500}
                  style={{
                    cursor: !isBuy && account && maxMud > 0 ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (!isBuy && account && maxMud > 0) {
                      setIndependentField('amount')
                      setTypedValue(trimTrailingZeros(maxMud.toFixed(4)))
                    }
                  }}
                >
                  {t('balance', {
                    balanceInput: account ? trimTrailingZeros(parseFloat(mudBalance).toFixed(4)) : '--'
                  })}
                </TYPE.body>
              </RowBetween>
            </LabelRow>
            <InputRow>
              <NumericalInput
                className="order-amount-input"
                value={
                  independentField === 'amount'
                    ? typedValue
                    : typedValue && priceNum > 0
                    ? formatDependentMud((parseFloat(typedValue) || 0) / priceNum)
                    : ''
                }
                onUserInput={handleAmountInput}
                placeholder="0.0"
              />
              <UnitLabel>MUD</UnitLabel>
            </InputRow>
          </Container>
        </InputPanel>

        {/* 交易额(USDT)：可输入，与数量(MUD)联动；买入时点击 USDT 余额填入全部 */}
        <InputPanel>
          <Container>
            <LabelRow>
              <RowBetween>
                <TYPE.body color={theme.text2} fontWeight={500} fontSize={14}>
                  {t('orderAmountTotal')}
                </TYPE.body>
                <TYPE.body
                  color={theme.text2}
                  fontSize={16}
                  fontWeight={500}
                  style={{
                    cursor: isBuy && account && usdtBalanceExact > 0 ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (isBuy && account && usdtBalanceExact > 0 && priceNum > 0) {
                      setIndependentField('usdtAmount')
                      setTypedValue(trimTrailingZeros(usdtBalanceExact.toFixed(4)))
                    }
                  }}
                >
                  {t('balance', { balanceInput: account ? trimTrailingZeros(usdtFormatted) : '--' })}
                </TYPE.body>
              </RowBetween>
            </LabelRow>
            <InputRow>
              <NumericalInput
                className="order-usdt-amount-input"
                value={
                  independentField === 'usdtAmount'
                    ? typedValue
                    : typedValue && priceNum > 0
                    ? formatDependentUsdt((parseFloat(typedValue) || 0) * priceNum)
                    : ''
                }
                onUserInput={handleUsdtAmountInput}
                placeholder="0.0"
              />
              <UnitLabel>USDT</UnitLabel>
            </InputRow>
          </Container>
        </InputPanel>

        {/* 手续费率：左侧文案，右侧数字 + 编辑按钮，纯文字无输入框感 */}
        <FeeRateRow>
          <TYPE.body color={theme.text2} fontWeight={500} fontSize={14}>
            {t('orderFeeRate')}
          </TYPE.body>
          <FeeRateRight>
            <span>{(feeRateNum / 10).toFixed(1)}%</span>
            <FeeRateChangeBtn
              type="button"
              onClick={() => setFeeRateModalOpen(true)}
              title={t('orderFeeRateChange')}
              aria-label={t('orderFeeRateChange')}
            >
              <Edit2 size={16} />
            </FeeRateChangeBtn>
          </FeeRateRight>
        </FeeRateRow>

        <FeeRateModal
          isOpen={feeRateModalOpen}
          onDismiss={() => setFeeRateModalOpen(false)}
          value={feeRate}
          options={feeOptions}
          onSelect={setFeeRate}
        />

        {/* 至少获得（合约保证按挂单价扣费后至少得到该数量） */}
        {estimatedReceive && (
          <>
            <InfoRow>
              <InfoLabel>{t('orderEstReceive')}</InfoLabel>
              <InfoValue>≥ {estimatedReceive}</InfoValue>
            </InfoRow>
          </>
        )}

        {/* Action Buttons */}
        <AutoColumn gap="6px" style={{ marginTop: 12 }}>
          {!account ? (
            <ButtonSecondary onClick={toggleWalletModal}>
              <Text fontWeight={500} fontSize={16}>
                {t('connectWallet')}
              </Text>
            </ButtonSecondary>
          ) : validationError ? (
            <ButtonSecondary disabled>
              <Text fontWeight={500} fontSize={16}>
                {validationError}
              </Text>
            </ButtonSecondary>
          ) : !priceInput || !typedValue ? (
            <ButtonSecondary disabled>
              <Text fontWeight={500} fontSize={16}>
                {t('enterAmount')}
              </Text>
            </ButtonSecondary>
          ) : isBuy && needsApproval ? (
            <ButtonSecondary onClick={handleApprove} disabled={approvePending}>
              <Text fontWeight={500} fontSize={16}>
                {approvePending ? t('orderApproving') : t('orderApproveUSDT')}
              </Text>
            </ButtonSecondary>
          ) : (
            <ButtonSecondary onClick={handleCreateOrder} disabled={isPending}>
              <Text fontWeight={500} fontSize={16}>
                {isPending ? t('orderCreating') : t('orderCreate')}
              </Text>
            </ButtonSecondary>
          )}
        </AutoColumn>
      </OrderWrapper>

      <TransactionConfirmationModal
        isOpen={txConfirmOpen}
        onDismiss={() => {
          setTxConfirmOpen(false)
          setTxConfirmHash(undefined)
        }}
        hash={txConfirmHash}
        attemptingTxn={txConfirmPending}
        pendingText={t('orderSubmitting')}
        content={() => null}
      />
    </>
  )
}
